import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CashRegister } from '../../../interfaces/cash-register';
import { PostCashPaymentRequest } from '../../../interfaces/payment';
import { BrandService } from '../../../services/brand/brand.service';
import { CashRegisterService } from '../../../services/cash-register/cash-register.service';
import { PaymentService } from '../../../services/payment/payment.service';
import { BrlPipe } from '../../../utils/brl.pipe';
import { brandNameOrDefault, formatBRL, formatDateTimeBR, openPrintWindow, receiptLogoHtml } from '../../../utils/print-receipt.util';

export interface ReciboEspecie {
  nrRecibo: number;
  valor: number;
}

/**
 * Recebe em dinheiro no caixa do operador. O recebimento vira adiantamento do paciente;
 * quem abriu o modal recarrega a conta e o "Processar Pagamento" vincula o adiantamento.
 */
@Component({
  selector: 'app-pagamento-especie-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, BrlPipe],
  // Sem modo escuro proprio: fica no tema claro mesmo com o app escuro.
  host: { class: 'tema-claro', style: 'display: contents' },
  templateUrl: './pagamento-especie-modal.component.html',
})
export class PagamentoEspecieModalComponent implements OnChanges {
  static readonly LIMITE_SANGRIA = 1000;

  @Input() aberto = false;
  @Input() clinicId = 0;
  @Input() userName = '';
  @Input() personId: number | string | null = null;
  /** Saldo da conta: base do troco. */
  @Input() valorConta = 0;
  /** Valor que já vem preenchido ao abrir. */
  @Input() valorSugerido: number | null = null;

  @Output() fechar = new EventEmitter<void>();
  @Output() registrado = new EventEmitter<ReciboEspecie>();

  caixas: CashRegister[] = [];
  caixaSelecionada: CashRegister | null = null;
  valor = 0;
  observacao = '';
  erro = '';
  saldoAbertoFeedback = '';
  recibo: ReciboEspecie | null = null;
  isCarregandoCaixas = false;
  isAbrindoSaldo = false;
  isRegistrando = false;
  isImprimindo = false;
  private idempotencyKey = '';

  constructor(
    private cashRegisterService: CashRegisterService,
    private paymentService: PaymentService,
    private brandService: BrandService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['aberto']?.currentValue && !changes['aberto'].previousValue) {
      this.iniciar();
    }
  }

  private async iniciar(): Promise<void> {
    this.valor = this.valorSugerido ?? this.valorConta ?? 0;
    this.observacao = '';
    this.erro = '';
    this.saldoAbertoFeedback = '';
    this.recibo = null;
    this.idempotencyKey = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `caixa-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    // Recarrega a cada abertura: balanceForToday pode ter mudado em outra aba ou PC.
    this.isCarregandoCaixas = true;
    await this.carregarCaixas();
    this.isCarregandoCaixas = false;
    this.caixaSelecionada = this.caixas[0] ?? null;
  }

  private async carregarCaixas(): Promise<void> {
    try {
      this.caixas = this.userName && this.clinicId
        ? await this.cashRegisterService.getByUnit(this.clinicId, this.userName)
        : [];
    } catch {
      this.caixas = [];
    }
  }

  get troco(): number {
    const recebido = this.valor || 0;
    return recebido > this.valorConta ? recebido - this.valorConta : 0;
  }

  get saldoCaixaProjetado(): number {
    // Troco volta para o paciente: só o que fica no caixa conta.
    return (this.caixaSelecionada?.currentBalance ?? 0) + (this.valor || 0) - this.troco;
  }

  get precisaSangria(): boolean {
    return !!this.caixaSelecionada
      && (this.valor || 0) > 0
      && this.saldoCaixaProjetado > PagamentoEspecieModalComponent.LIMITE_SANGRIA;
  }

  get formValido(): boolean {
    return !!this.caixaSelecionada?.balanceSequence
      && this.caixaSelecionada.balanceForToday
      && (this.valor || 0) > 0
      && !!this.personId;
  }

  async abrirSaldoHoje(): Promise<void> {
    if (!this.caixaSelecionada || this.isAbrindoSaldo) return;
    this.isAbrindoSaldo = true;
    this.erro = '';
    this.saldoAbertoFeedback = '';
    try {
      // Revalida antes de abrir: evita duplicar saldo quando outra aba/PC já abriu.
      await this.carregarCaixas();
      this.caixaSelecionada = this.caixas.find(c => c.sequence === this.caixaSelecionada!.sequence) ?? this.caixaSelecionada;

      if (this.caixaSelecionada.balanceForToday && this.caixaSelecionada.balanceSequence) {
        this.saldoAbertoFeedback = `Saldo de hoje já está aberto (seq ${this.caixaSelecionada.balanceSequence}).`;
        return;
      }

      const result = await this.cashRegisterService.openBalance({
        cashRegisterSequence: this.caixaSelecionada.sequence,
        username: this.userName,
      });
      await this.carregarCaixas();
      this.caixaSelecionada = this.caixas.find(c => c.sequence === this.caixaSelecionada!.sequence) ?? this.caixaSelecionada;
      this.saldoAbertoFeedback = `Saldo aberto com sucesso (seq ${result.balanceSequence}).`;
    } catch (e: any) {
      this.erro = e?.error?.feedback || e?.message || 'Erro ao abrir saldo do caixa.';
    } finally {
      this.isAbrindoSaldo = false;
    }
  }

  async registrar(): Promise<void> {
    if (!this.formValido || this.isRegistrando) return;
    this.isRegistrando = true;
    this.erro = '';
    try {
      // Troco é dinheiro devolvido ao paciente, não vira adiantamento.
      const valorAPagar = this.troco > 0 ? this.valorConta : this.valor;
      const request: PostCashPaymentRequest = {
        nrSeqSaldoCaixa: this.caixaSelecionada!.balanceSequence!,
        patientId: String(this.personId),
        amount: valorAPagar,
        establishmentId: this.caixaSelecionada!.establishmentId,
        username: this.userName,
        observation: this.observacao?.trim() || undefined,
      };
      const result = await this.paymentService.postCashPayment(this.idempotencyKey, request);
      this.recibo = { nrRecibo: result.nrRecibo, valor: valorAPagar };
      this.registrado.emit(this.recibo);
    } catch (e: any) {
      const status = e?.status;
      if (status === 0) {
        this.erro = 'Sem conexão. Verifique a rede e tente novamente.';
      } else if (status === 401 || status === 403) {
        this.erro = 'Sessão expirada. Faça login novamente.';
      } else {
        this.erro = e?.error?.feedback || e?.message || 'Erro ao registrar pagamento em espécie.';
      }
    } finally {
      this.isRegistrando = false;
    }
  }

  fecharModal(): void {
    if (this.isRegistrando) return;
    this.fechar.emit();
  }

  async imprimirRecibo(): Promise<void> {
    if (!this.recibo || this.isImprimindo) return;
    this.isImprimindo = true;
    try {
      const recibo = await this.paymentService.getCashReceipt(this.recibo.nrRecibo);
      if (!recibo) {
        this.erro = 'Não foi possível carregar os dados do recibo.';
        return;
      }
      const linha = (rotulo: string, valor: number) =>
        valor > 0 ? `<tr><td class="label">${rotulo}</td><td class="value">${formatBRL(valor)}</td></tr>` : '';
      const linhaCpf = recibo.nrCpf ? `<tr><td class="label">CPF</td><td class="value">${this.formatarCpf(recibo.nrCpf)}</td></tr>` : '';
      const html = `
        <div class="header">
          <div class="logo">${receiptLogoHtml(this.brandService.logo, this.brandService.cdEmpresa)}</div>
          <div style="text-align: right;">
            <p class="title">Recibo de Pagamento</p>
            <p class="subtitle">Pagamento em espécie</p>
            <p class="receipt-number">Nº ${recibo.nrRecibo}</p>
          </div>
        </div>
        <table class="data">
          <tr><td class="label">Pessoa</td><td class="value">${recibo.nmPessoa || '—'}</td></tr>
          ${linhaCpf}
          <tr><td class="label">Data/Hora</td><td class="value">${formatDateTimeBR(recibo.dtRecebimento)}</td></tr>
          <tr><td class="label">Transação</td><td class="value">${recibo.dsTransFin || '—'}</td></tr>
          <tr><td class="label">Usuário</td><td class="value">${this.userName}</td></tr>
        </table>
        <div class="valor-box">
          <div style="font-size: 10px; color: #626272;">Valor recebido</div>
          <div class="amount">${formatBRL(recibo.vlRecibo)}</div>
          ${recibo.dsValor ? `<div class="extenso">${recibo.dsValor}</div>` : ''}
        </div>
        <table class="data" style="margin-top: 10px;">
          <tr><td class="label">Espécie</td><td class="value">${formatBRL(recibo.vlEspecie)}</td></tr>
          ${linha('Cheque', recibo.vlCheque)}
          ${linha('Cartão Crédito', recibo.vlCartaoCre)}
          ${linha('Cartão Débito', recibo.vlCartaoDeb)}
        </table>
        <div class="signature">
          <div class="line"></div>
          <div class="name">Assinatura do responsável</div>
        </div>
        <div class="footer">${brandNameOrDefault(this.brandService.brandName)} — Comprovante sem valor fiscal</div>
      `;
      openPrintWindow(`Recibo ${recibo.nrRecibo}`, html);
    } catch {
      this.erro = 'Erro ao carregar recibo.';
    } finally {
      this.isImprimindo = false;
    }
  }

  private formatarCpf(cpf: string): string {
    const d = (cpf ?? '').replace(/\D/g, '');
    return d.length === 11 ? `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}` : cpf || '—';
  }
}
